/* ===========================================================
   panels/DocSide.jsx — around the page (his asks, 2026-10-28)

   TabStrip     tabs under the toolbar: click to switch, + to add,
                double-click to rename, right-click for more
   Outline      on the left like Google Docs: the tabs, the open tab's
                headings (click to jump), then the other docs in this
                doc's group
   PreviewPane  a live HTML/CSS/JS block's preview beside the doc (the
                doc moves left); full screen with one click, Esc back
   =========================================================== */

import { useEffect, useRef, useState } from 'preact/hooks';
import { Icon, IconButton, promptDialog, confirmDialog } from '../ui/ui.jsx';
import { registerContext } from '../ui/context.jsx';
import { go } from '../lib/router.js';
import { docs } from '../state.js';
import { groups } from '../sync/docs.js';
import { tabsOf, addTab, renameTab, moveTab, deleteTab, headingsIn } from '../editor/tabs.js';

/** The tab list, kept live (someone else can add a tab while you're in). */
export function useTabs(sync) {
  const [tabs, setTabs] = useState(() => (sync ? tabsOf(sync.doc) : []));
  useEffect(() => {
    if (!sync) return;
    const arr = sync.doc.getArray('tabs');
    const on = () => setTabs(tabsOf(sync.doc));
    on();
    arr.observe(on);
    return () => arr.unobserve(on);
  }, [sync]);
  return tabs;
}

/* ---------- tabs under the toolbar ---------- */

const tabCtx = { sync: null, pick: null, canEdit: false };

async function renameAsk(sync, t) {
  const name = await promptDialog({ title: 'Rename tab', label: 'Name', value: t.title });
  if (name?.trim()) renameTab(sync.doc, t.id, name);
}

function tabItems(t) {
  const { sync, pick, canEdit } = tabCtx;
  if (!sync) return null;
  const list = tabsOf(sync.doc);
  const i = list.findIndex(x => x.id === t.id);
  return [
    { label: 'Open', icon: 'open', onSelect: () => pick(t.id) },
    canEdit && !t.kind && { label: 'Rename', icon: 'pencil', onSelect: () => renameAsk(sync, t) },
    canEdit && i > 0 && { label: 'Move left', icon: 'back', onSelect: () => moveTab(sync.doc, t.id, -1) },
    canEdit && i < list.length - 1 && { label: 'Move right', icon: 'chevron', onSelect: () => moveTab(sync.doc, t.id, 1) },
    canEdit && t.id !== 'default' && 'sep',
    canEdit && t.id !== 'default' && { label: 'Delete tab', icon: 'trash', danger: true, onSelect: async () => {
      if (!await confirmDialog({ title: `Delete "${t.title}"?`, text: 'Its text goes with it (versions in History still have it).', ok: 'Delete tab', danger: true })) return;
      deleteTab(sync.doc, t.id);
      pick('default');
    } },
  ];
}
registerContext('.doctab[data-tab]', el => {
  const t = tabCtx.sync && tabsOf(tabCtx.sync.doc).find(x => x.id === el.dataset.tab);
  return t ? tabItems(t) : null;
});

export function TabStrip({ sync, tabs, active, onPick, canEdit }) {
  tabCtx.sync = sync; tabCtx.pick = onPick; tabCtx.canEdit = canEdit;
  return (
    <div class="doctabs" role="tablist" aria-label="Tabs in this doc">
      {tabs.map(t => (
        <button type="button" role="tab" key={t.id} data-tab={t.id} class={`doctab ${t.kind ? t.kind : ''}`}
          aria-selected={String(t.id === active)} onClick={() => onPick(t.id)}
          onDblClick={() => canEdit && !t.kind && renameAsk(sync, t)} title={t.title}>
          {t.kind === 'claude' && <Icon name="sparkle" size={13} />}{t.kind === 'tasks' && <Icon name="checklist" size={13} />}
          <span>{t.title}</span>
        </button>
      ))}
      {canEdit && (
        <button type="button" class="doctab add" aria-label="Add a tab" title="Add a tab" onClick={() => onPick(addTab(sync.doc).id)}>
          <Icon name="plus" size={15} />
        </button>
      )}
    </div>
  );
}

/* ---------- the outline on the left ---------- */

export function Outline({ editor, tabs, active, onPick, docId, onClose }) {
  const [heads, setHeads] = useState(() => headingsIn(editor));
  useEffect(() => {
    if (!editor) return;
    let t = null;
    const on = () => { clearTimeout(t); t = setTimeout(() => setHeads(headingsIn(editor)), 250); };
    setHeads(headingsIn(editor));
    editor.on('update', on);
    return () => { clearTimeout(t); editor.off('update', on); };
  }, [editor]);

  const jump = h => {
    editor.chain().focus().setTextSelection(h.pos + 1).run();
    const el = editor.view.nodeDOM(h.pos);
    el?.scrollIntoView?.({ block: 'start', behavior: 'smooth' });
  };

  const me = docs.value.find(d => d.id === docId);
  const group = me?.groupId && groups.value.find(g => g.id === me.groupId);
  const siblings = group ? docs.value.filter(d => d.groupId === group.id && !d.deletedAt) : [];

  return (
    <aside class="doc-outline" aria-label="Outline">
      <div class="outline-head">
        <span class="outline-label">Outline</span>
        <IconButton icon="close" label="Hide the outline" onClick={onClose} />
      </div>
      <nav>
        {tabs.map(t => (
          <div class="outline-tab" key={t.id}>
            <button type="button" class={`outline-tabname ${t.id === active ? 'on' : ''}`} onClick={() => onPick(t.id)}>
              <Icon name={t.kind === 'claude' ? 'sparkle' : t.kind === 'tasks' ? 'checklist' : 'file'} size={15} /><span>{t.title}</span>
            </button>
            {t.id === active && (
              heads.length ? (
                <ul class="outline-heads">
                  {heads.map(h => (
                    <li key={h.pos}><button type="button" class={`lv${Math.min(h.level, 4)}`} onClick={() => jump(h)}>{h.text}</button></li>
                  ))}
                </ul>
              ) : <p class="outline-empty">Headings you add show up here.</p>
            )}
          </div>
        ))}
      </nav>
      {group && (
        <div class="outline-group">
          <span class="outline-label"><Icon name="folder" size={14} /> {group.name}</span>
          <ul>
            {siblings.map(d => (
              <li key={d.id}>
                <a href={`/d/${d.id}`} class={d.id === docId ? 'on' : ''} onClick={e => { e.preventDefault(); if (d.id !== docId) go(`/d/${d.id}`); }}>
                  <Icon name="doc" size={14} /><span>{d.title || 'Untitled document'}</span>
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}
    </aside>
  );
}

/* ---------- the live preview beside the doc ---------- */

export function PreviewPane({ code, onClose }) {
  const [full, setFull] = useState(false);
  const [stuck, setStuck] = useState(false);
  const [lines, setLines] = useState([]);
  const stage = useRef();
  const run = useRef(0);

  useEffect(() => {
    if (!full) return;
    const esc = e => { if (e.key === 'Escape') { e.stopPropagation(); setFull(false); } };
    window.addEventListener('keydown', esc, true);
    return () => window.removeEventListener('keydown', esc, true);
  }, [full]);

  // same walled-off runner as the inline preview (no cookies, no storage, no API)
  useEffect(() => {
    let frame = null, beat = Date.now(), dog = null;
    const timer = setTimeout(() => {
      const id = ++run.current;
      setStuck(false); setLines([]);
      frame = document.createElement('iframe');
      frame.className = 'pv-frame';
      frame.setAttribute('sandbox', 'allow-scripts');
      frame.setAttribute('referrerpolicy', 'no-referrer');
      frame.title = 'Live preview';
      frame.src = `/sandbox/preview#${id}`;
      stage.current?.replaceChildren(frame);
      beat = Date.now();
      dog = setInterval(() => {
        if (Date.now() - beat > 5000 && frame) { frame.remove(); frame = null; clearInterval(dog); setStuck(true); }
      }, 1500);
    }, 400);
    const onMsg = e => {
      if (!frame || e.source !== frame.contentWindow) return;
      const m = e.data || {};
      if (m.run !== run.current) return;
      if (m.type === 'ready') frame.contentWindow.postMessage({ type: 'render', run: run.current, ...code }, '*');
      else if (m.type === 'alive') beat = Date.now();
      else if (m.type === 'console') setLines(l => [...l.slice(-99), { level: m.level, text: String(m.text).slice(0, 2000) }]);
    };
    window.addEventListener('message', onMsg);
    return () => { clearTimeout(timer); clearInterval(dog); window.removeEventListener('message', onMsg); frame?.remove(); };
  }, [code.html, code.css, code.js]);

  return (
    <section class={`preview-pane ${full ? 'pv-full' : ''}`} aria-label="Live preview">
      <div class="pv-head">
        <Icon name="sandbox" size={16} /><b>Live preview</b>
        <span class="grow" />
        <IconButton icon={full ? 'close' : 'open'} label={full ? 'Leave full screen (Esc)' : 'Full screen'} onClick={() => setFull(f => !f)} />
        {!full && <IconButton icon="close" label="Close the preview" onClick={onClose} />}
      </div>
      <div class="pv-stage" ref={stage} />
      {stuck && <div class="pv-stuck"><p>This preview got stuck (an endless loop, maybe?), so I stopped it. Change the code to try again.</p></div>}
      {lines.length > 0 && (
        <details class="pv-console" open>
          <summary>Console</summary>
          <div role="log" aria-live="polite">{lines.map((l, i) => <div key={i} class={`sb-line l-${l.level}`}>{l.text}</div>)}</div>
        </details>
      )}
    </section>
  );
}

